import { useId } from "react";

/**
 * §B7.1: every input has a bound `<label>`; a placeholder is never a label. The hint and the error are bound
 * through `aria-describedby`, so a screen reader hears why a value was rejected. §3: label above (500 13),
 * hint below (12 muted), error turns the control border `--color-fail-mark` and the message `--color-fail-text`.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  labelAside,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** A quiet link beside the label ("Forgot password?" on SignIn.html). */
  labelAside?: React.ReactNode;
  children: (props: { id: string; describedBy: string | undefined; required: boolean }) => React.ReactNode;
}): JSX.Element {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined || error === null ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter((value) => value !== undefined).join(" ") || undefined;
  const hasError = error !== undefined && error !== null;
  return (
    <div className={hasError ? "field field--error" : "field"}>
      {labelAside === undefined ? (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      ) : (
        <div className="field__labelrow">
          <label className="field__label" htmlFor={id}>
            {label}
          </label>
          {labelAside}
        </div>
      )}
      {children({ id, describedBy, required })}
      {hint === undefined ? null : (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {hasError ? (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
